import { Link } from "@tanstack/react-router";
import { LogOut, Settings } from "lucide-react";

import { AdaStatLink } from "@/components/adastat-link";
import { PlainValue } from "@/components/plain-value";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useGetExecuteTrade } from "@/generated/api/arbitrages/arbitrages";
import { useGetRegulusPoint } from "@/generated/api/regulus/regulus";
import { authRequired, signOut } from "@/lib/auth/session";
import { useLiveStatus } from "@/lib/events/live-events";
import type { StreamStatus } from "@/lib/events/stream";
import { cn } from "@/lib/utils/cn";

import { TopNav } from "./nav";

// The point refreshes on every block the bot pushes (`chain_advanced`), so the header neither
// polls nor offers a refresh button.
export function AppHeader() {
    const point = useGetRegulusPoint();
    const trading = useGetExecuteTrade();

    return (
        <header className="sticky top-0 z-30 border-b bg-background/95 backdrop-blur">
            <div className="flex items-center gap-3 px-3 py-2.5 sm:px-4 md:px-6 lg:px-8">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                        <span className="text-base font-semibold">Arb bot</span>
                        <LiveIndicator status={useLiveStatus()} />
                        {/* Read-only on purpose: trading is a deploy-time setting, never a web control. */}
                        {trading.data !== undefined && (
                            <Badge variant={trading.data ? "warning" : "default"}>
                                Trading: {trading.data ? "on" : "off"}
                            </Badge>
                        )}
                    </div>
                    {point.data ? (
                        <p
                            className="flex min-w-0 items-center font-mono text-xs whitespace-pre text-muted-foreground sm:text-sm"
                            data-testid="current-point"
                        >
                            <span className="shrink-0">slot </span>
                            <PlainValue value={point.data.slot} label="Slot" className="shrink-0" />
                            <span className="shrink-0"> · block </span>
                            <AdaStatLink
                                kind="block"
                                value={point.data.id}
                                label="Block id"
                                fit="line"
                                className="min-w-0 text-inherit sm:text-sm"
                            />
                        </p>
                    ) : (
                        <p className="text-xs text-muted-foreground sm:text-sm">
                            {point.error ? "point unavailable" : "loading point…"}
                        </p>
                    )}
                </div>
                <div className="flex shrink-0 items-center gap-1 sm:gap-2">
                    <Button size="icon-sm" variant="ghost" asChild>
                        <Link to="/settings">
                            <Settings />
                            <span className="sr-only lg:not-sr-only">Settings</span>
                        </Link>
                    </Button>
                    {authRequired() && (
                        <Button size="icon-sm" variant="ghost" onClick={() => signOut("logout")}>
                            <LogOut />
                            <span className="sr-only lg:not-sr-only">Logout</span>
                        </Button>
                    )}
                </div>
            </div>
            <div className="hidden border-t px-3 py-1.5 sm:px-4 md:block md:px-6 lg:px-8">
                <TopNav />
            </div>
        </header>
    );
}

const LIVE_LABELS: Record<StreamStatus, string> = {
    connecting: "Connecting to live updates",
    live: "Live updates on",
    reconnecting: "Live updates lost, reconnecting",
};

// With no refresh button, this dot is how the operator knows the page is current.
function LiveIndicator({ status }: { status: StreamStatus }) {
    return (
        <span
            role="status"
            aria-label={LIVE_LABELS[status]}
            title={LIVE_LABELS[status]}
            data-status={status}
            className={cn(
                "size-2 shrink-0 rounded-full",
                status === "live" ? "bg-success" : "animate-pulse bg-warning",
            )}
        />
    );
}
